import React from "react";
import classNames from "classnames";
// reactstrap components
import {
	ButtonGroup,
	Button,
	Card,
	CardBody,
	CardHeader,
	CardTitle,
	Row,
	Col,
	FormGroup,
	Label,
	Input,
	Alert
} from "reactstrap";
import Datetime from "react-datetime";
import Async from 'react-select/async';
import moment from "moment";
import Select from 'react-select';
import { getFormattedDate } from "helpers/date";
import { formatPaymentType, formatPaymentSubtype } from "helpers/payment";
import { formatPaymentStatus } from "helpers/status";
import { paymentTypes, paymentStatuses } from "../../constants";
import CurrencyInput from "components/CurrencyInput/CurrencyInput";
import ProjectContext from "contexts/ProjectContext";
// API
import { getProjectsRequest } from "api/project";
import { getSellersRequest } from "api/seller";
import { getClientsRequest } from "api/client";
import { 
	getPaymentRequest, 
	createPaymentRequest, 
	updatePaymentRequest
} from "api/payment";


class PaymentForm extends React.Component {

	constructor(props) {
		super(props);
		this.state = {
			payment: null,
			error: null,
			saving: false,
			bigChartData: "singlePayment",
			filtersClients: {},
			filtersProjects: {},
			statusWarning: false,
			sellers: []
		}
	}

	static contextType = ProjectContext;

	async componentDidMount() {
		const params = new URLSearchParams(this.props.location.search);
		const id = params.get('id');

		if (id) {
			try {
				const payment = await getPaymentRequest(id);

				if(payment.project) {
					payment.selectedProject = {
						label: `${payment.project.name}`,
						value: payment.project._id
					};
				}

				if(payment.client) {
					payment.selectedClient = {
						label: `${payment.client.first_name} ${payment.client.last_name}`,
						value: payment.client._id
					};
					payment.selectedType = {
						label: `${formatPaymentType(payment.type)}`,
						value: payment.type
					};
					payment.selectedSubtype = {
						label: `${formatPaymentSubtype(payment.subtype)}`,
						value: payment.subtype
					};
					payment.selectedStatus = {
						label: `${formatPaymentStatus(payment.status)}`,
						value: payment.status
					}
				}

				const sellersResponse =	await getSellersRequest();

				const { sellers } = sellersResponse;

				this.setState({ payment, sellers })
			} catch(err) {
				this.setState({
					error: err.message || 'There was an error fetching payment'
				});
			}
		} else {

			const sellersResponse =	await getSellersRequest();
			const { sellers } = sellersResponse;

			// case for adding a payment
			this.setState({
				payment: {
					type: '',
					creationType: "singlePayment"
				},
				sellers
			})
		}
	}

	goTo = path => this.props.history.push(path);

	onChangeField = (e) => {
		const { target } = e;
		const { name, value } = target;

		this.setState(prevState => ({
			payment: {
				...prevState.payment,
				[name]: value,
			}
		}));
	}

	onChangeCheckbox = (e) => {
		const { target } = e;
		const { name, checked } = target;

		this.setState(prevState => ({
			payment: {
				...prevState.payment,
				[name]: checked,
			},
		}));
	}

	save = async () => {
		this.setState({
			saving: true
		})

		const { payment } = this.state;
		const { _id } = payment;

		if(payment.check_number && !payment.bank_name) {
			return this.setState({
				error: 'Please specify the name of the bank before proceeding',
				saving: false
			});
		}

		if(payment.firstPrincipalCheckNumber && !payment.firstPrincipalBankName) {
			return this.setState({
				error: 'Please specify the principal check bank name before proceeding',
				saving: false
			});
		}

		if(payment.firstMiscCheckNumber && !payment.firstMiscBankName) {
			return this.setState({
				error: 'Please specify the miscellaneous check bank name before proceeding',
				saving: false
			});
		}

		if(payment.firstRentalCheckNumber && !payment.firstRentalBankName) {
			return this.setState({
				error: 'Please specify the rental check bank name before proceeding',
				saving: false
			});
		}

		if(payment.selectedSubtype) {
			payment.subtype = payment.selectedSubtype.value;
		}

		if(payment.selectedType) {
			payment.type = payment.selectedType.value;
		}

		if(payment.selectedStatus) {
			payment.status = payment.selectedStatus.value;
		}

		payment.total = this.calculateTotalAmount();

		try {
			if (_id) {
				await updatePaymentRequest(payment);
			} else {
				await createPaymentRequest(payment);
			}
			this.setState({
				saving: false
			})
			this.goTo('/payments');
		} catch (err) {
			console.log(err, "error");
			this.setState({
				error: err.message || 'There was an error saving payment',
				saving: false
			})
		}
	}

	onSelectChange = (key) => (object) => {

		const statusWarning = key === "selectedStatus" && object.value ==="completed" ? true : false;

		this.setState(prevState => ({
			payment: {
				...prevState.payment,
				[key]: object,
			},
			statusWarning
		}));
	}

	setBgChartData = name => {
		this.setState(prevState => ({
			payment: {
				...prevState.payment,
				creationType: name
			},
			bigChartData: name
		}));
	};

	loadClients = async (search, callback) => {
		const { filtersClients } = this.state;
		const { selectedProject } = this.context;
	
		filtersClients.project = selectedProject;

		this.setState({
			filtersClients: {
				...filtersClients,
				search
			}
		})
		if(search) {
			const clientResponse = await getClientsRequest(filtersClients);
			const clients = clientResponse.clients.map(client => {
				return {
					data: client,
					label: `${client.first_name} ${client.last_name}`,
					value: client._id
				}
			})
			callback(clients);
		}
	}

	loadProjects = async (search, callback) => {
		const { filtersProjects } = this.state;

		this.setState({
			filtersProjects: {
				...filtersProjects,
				search
			}
		})
		if(search) {
			const projectResponse = await getProjectsRequest(filtersProjects);
			const projects = projectResponse.projects.map(project => {
				return {
					data: project,
					label: `${project.name}`,
					value: project._id
				}
			})
			callback(projects);
		}
	}

	calculateTotalAmount = () => {
		const {
			amount,
			extras,
			loan_interest
		} = this.state.payment;
		const entries = [amount, extras, loan_interest].filter(item => !!Number(item));
		const totalEntries = entries.length ? entries.reduce((item1, item2) => Number(item1)+Number(item2)) : 0;
		return totalEntries;
	}

	render() {

		const {
			payment = {},
			error = null,
			saving,
			bigChartData,
			statusWarning,
			sellers
		} = this.state;

		const {
			_id,
			selectedType = [],
			selectedSubtype = [],
			selectedStatus = [],
			selectedClient = [],
			amount,
			extras,
			extras_description,
			due_date,
			settled_date,
			generateDownPayments,
			totalDownPaymentMonths,
			firstDownPaymentDate,
			principalAmount,
            miscelaneousAmount,
            rentalAmount,
			generateBalancePayment,
			totalBalancePaymentMonths,
			balanceAmount,
			balancePaymentDate,
			generateLoanDifferencePayments,
			totalLoanDifferenceMonths,
			firstLoanDifferencePaymentDate,
			loanDifferenceAmount,
			generatePenaltyPayments,
			totalPenaltyMonths,
			firstPenaltyPaymentDate,
			penaltyAmount,
			generateCounterOfferPayments,
			totalCounterOfferMonths,
			firstCounterOfferPaymentDate,
			counterOfferAmount,
			created_at,
			updated_at,
			check_number,
			bank_name,
			si_number,
			firstPrincipalCheckNumber,
			firstMiscCheckNumber,
			firstRentalCheckNumber,
			firstPrincipalBankName = '',
			firstMiscBankName = '',
			firstRentalBankName = '',
			description,
			editNote,
			is_ar,
			selectedProject,
			loan_interest,
			balanceCheckNumber,
			balanceCheckBankName
		} = payment || {};

		const paymentTypesOptions = paymentTypes.map((type) => {
			return {
				label: formatPaymentType(type),
				value: type
			}
		})

		const paymentStatusOptions = paymentStatuses.map((status) => {
			return {
				label: formatPaymentStatus(status),
				value: status
			}
		})

		const selectedSeller = payment && payment.seller && (typeof payment.seller === 'object' ? payment.seller._id : payment.seller);

		return (
			<>
				<div className="content">
					<Row>
						<Col md="12">
							<Card>
								<CardHeader className="mb-5">
									<CardTitle tag="h3">
										Payment
                  					</CardTitle>
									<Button
										className="float-right"
										color="default"
										onClick={() => this.goTo('/payments')}
									>
										Back
                  					</Button>

									  <Row>
										<Col sm="6">
										{!_id &&
											<ButtonGroup
												className="btn-group-toggle float-right"
												data-toggle="buttons"
											>
												<Button
													color="info"
													id="0"
													size="lg"
													tag="label"
													className={classNames("btn-simple", {
														active: this.state.bigChartData === "singlePayment"
													})}
													onClick={() => this.setBgChartData("singlePayment")}
												>
													<input defaultChecked name="options" type="radio" />
													<span className="d-none d-sm-block d-md-block d-lg-block d-xl-block">
														Single Payment
													</span>
													<span className="d-block d-sm-none">
														<i className="tim-icons icon-single-02" />
													</span>
												</Button>
												<Button
													color="info"
													id="1"
													size="lg"
													tag="label"
													className={classNames("btn-simple", {
														active: this.state.bigChartData === "bulkPayments"
													})}
													onClick={() => this.setBgChartData("bulkPayments")}
												>
													<input name="options" type="radio" />
													<span className="d-none d-sm-block d-md-block d-lg-block d-xl-block">
														Bulk Payments
													</span>
													<span className="d-block d-sm-none">
														<i className="tim-icons icon-gift-2" />
													</span>
												</Button>
											</ButtonGroup>
										}
										</Col>
									</Row>								
								</CardHeader>
								<CardBody>
									{!payment
										? <div>Loading...</div>
										: bigChartData === "singlePayment"
											? <>
												<FormGroup>
													<Label>Client <span className="required-start">*</span></Label>
													<Async
														value={selectedClient}
														name="selectedClient"
														options={[]}
														loadOptions={this.loadClients}
														className="basic-multi-select"
														classNamePrefix="select"
														onChange={this.onSelectChange('selectedClient')}
														placeholder="Search client by name or email"
													/>
												</FormGroup>
												<FormGroup>
													<Label>Type <span className="required-start">*</span></Label>
													<Select
														value={selectedType}
														name="selectedType"
														options={paymentTypesOptions}
														className="basic-multi-select"
														classNamePrefix="select"
														onChange={this.onSelectChange('selectedType')}
													/>
												</FormGroup>
												{selectedType && selectedType.value === "downPayment" &&
													<FormGroup>
														<Label>Subtype <span className="required-start">*</span></Label>
														<Select
															value={selectedSubtype}
															name="selectedSubtype"
															options={[
																{
																	label: "Principal",
																	value: "principal"
																},
																{
																	label: "Miscelaneous",
																	value: "miscelaneous"
																},
																{
																	label: "Rental",
																	value: "rental"
																},
																{
																	label: "Reservation",
																	value: "reservation"
																},
																{
																	label: "Spot",
																	value: "spot"
																}
															]}
															className="basic-multi-select"
															classNamePrefix="select"
															onChange={this.onSelectChange('selectedSubtype')}
														/>
													</FormGroup>
												}
												{selectedType && 
												selectedType.value === "balancePayment" &&
													<FormGroup>
														<Label>Subtype <span className="required-start">*</span></Label>
														<Select
															value={selectedSubtype}
															name="selectedSubtype"
															options={[
																{
																	label: "Single",
																	value: "single"
																},
																{
																	label: "Loan Difference",
																	value: "loan difference"
																},
																{
																	label: "Penalty",
																	value: "penalty"
																},
																{
																	label: "Counter Offer",
																	value: "counter offer"
																},
															]}
															className="basic-multi-select"
															classNamePrefix="select"
															onChange={this.onSelectChange('selectedSubtype')}
															isDisabled={!selectedClient.value}
														/>
														{!selectedClient.value &&
															<Alert color="danger">
																Please select a client before selecting a subtype.
															</Alert>
														}
													</FormGroup>
												}
												{selectedType && 
												selectedType.value === "penaltyPayment" &&
													<FormGroup>
														<Label>Subtype <span className="required-start">*</span></Label>
														<Select
															value={selectedSubtype}
															name="selectedSubtype"
															options={[
																{
																	label: "Single",
																	value: "single"
																}
															]}
															className="basic-multi-select"
															classNamePrefix="select"
															onChange={this.onSelectChange('selectedSubtype')}
														/>
													</FormGroup>
												}
												<FormGroup>
													<Label>Amount <span className="required-start">*</span></Label>
													<CurrencyInput 
														name="amount"
														price={amount || 0}
														onChange={(value) => this.onChangeField({ target: { name: "amount", value } })}
														placeholder="Amount"
													/>
												</FormGroup>
												{selectedType && 
												selectedType.value === "balancePayment" && 
												selectedSubtype && selectedSubtype.value === "loan difference" ? <></> :
												<FormGroup>
													<Label>Extras</Label>
													<CurrencyInput 
														name="extras"
														price={extras || 0}
														onChange={(value) => this.onChangeField({ target: { name: "extras", value } })}
														placeholder="Extras (Admin fees, penalties, etc)"
													/>
												</FormGroup>
												}
												{extras ?
													<FormGroup>
														<Label>Extras: Description <span className="required-start">*</span></Label>
														<Input
															type="text"
															name="extras_description"
															value={extras_description}
															onChange={this.onChangeField}
															placeholder="Give a description about the extra value added"
														/>
													</FormGroup>
													: null
												}
												<FormGroup>
													<Label>Total</Label>
													<CurrencyInput 
														readOnly
														name="total"
														onChange={(value) => this.onChangeField({ target: { name: "total", value } })}
														price={this.calculateTotalAmount() || 0}
													/>
												</FormGroup>
												{selectedType && 
													selectedType.value === "penaltyPayment" &&  
													<FormGroup>
														<Label>Description</Label>
														<Input
															type="text"
															name="description"
															value={description}
															onChange={this.onChangeField}
														/>
												  	</FormGroup>
												}
												<FormGroup>
													<Label>Status <span className="required-start">*</span></Label>
													<Select
														value={selectedStatus}
														name="selectedStatus"
														options={paymentStatusOptions}
														className="basic-multi-select"
														classNamePrefix="select"
														onChange={this.onSelectChange('selectedStatus')}
													/>
													{selectedStatus.value === 'completed' && statusWarning ? 
													<Alert color="warning" className="mt-2" fade={false}>
														Warning: Please use the "Mark Payment as completed" modal 
														instead of setting the status as "completed" directly on this page.
													</Alert>
													: null }
												</FormGroup>
												<Row>
													<Col sm='6'>
														<FormGroup>
															<Label>Due Date <span className="required-start">*</span></Label>
															<Datetime
																inputProps={{
																	className: "form-control",
																	placeholder: "Due Date",
																	name: "due_date"
																}}
																dateFormat="MM/DD/YYYY"
																timeFormat={false}
																value={typeof due_date === 'string' ? moment(due_date).format("MM/DD/YYYY") : due_date}
																onChange={(date) => this.onChangeField({target: { value: moment(date).format("MM/DD/YYYY"), name: "due_date" }})}
																closeOnSelect={true}
															/>
														</FormGroup>
													</Col>
													<Col sm='6'>
														<FormGroup>
															<Label>Settle Date</Label>
															<Datetime
																inputProps={{
																	className: "form-control",
																	placeholder: "Settle Date",
																	name: "settled_date"
																}}
																dateFormat="MM/DD/YYYY"
																timeFormat={false}
																value={typeof settled_date === 'string' ? moment(settled_date).format("MM/DD/YYYY") : settled_date}
																onChange={(date) => this.onChangeField({target: { value: moment(date).format("MM/DD/YYYY"), name: "settled_date" }})}
																closeOnSelect={true}
															/>
														</FormGroup>
													</Col>
												</Row>
												<FormGroup>
													<Label>Check Number</Label>
													<Input
														type="number"
														name="check_number"
														value={check_number}
														onChange={this.onChangeField}
													/>
													<span className="form-text">
														{`Leave blank if payment method is CASH`}
													</span>
												</FormGroup>
												{!!check_number ? <FormGroup>
													<Label>Bank Name</Label>
													<Input
														type="text"
														name="bank_name"
														value={bank_name}
														onChange={this.onChangeField}
													/>
												</FormGroup> : null}
												<FormGroup check className="my-2">
													<Label check>
														<Input 
															type="checkbox"
															name="is_ar"
															value={is_ar}
															checked={is_ar}
															onChange={this.onChangeCheckbox}
														/>
														<span className="form-check-sign" />
														Is an AR number?
													</Label>
												</FormGroup>
												<FormGroup>
													<Label>{is_ar ? "AR Number" : "SI Number"}</Label>
													<Input
														type="text"
														name="si_number"
														value={si_number}
														onChange={this.onChangeField}
													/>
												</FormGroup>
												<FormGroup>
													<Label>Seller</Label>
													<Input 
														type="select"
														value={selectedSeller}
														onChange={this.onChangeField}
														name="seller"
													>
														<option value="">Select Seller</option>
														{
															sellers.map(seller => {
																return (
																	<option key={seller._id} value={seller._id}>{seller.first_name} {seller.last_name}</option>
																)
															})
														}
													</Input>
												</FormGroup>
											  </>
											: <>
												<FormGroup>
													<Label>Client <span className="required-start">*</span></Label>
													<Async
														value={selectedClient}
														name="selectedClient"
														options={[]}
														loadOptions={this.loadClients}
														className="basic-multi-select"
														classNamePrefix="select"
														onChange={this.onSelectChange('selectedClient')}
														placeholder="Search client by name or email"
													/>
												</FormGroup>
												<FormGroup>
													<Label>Seller</Label>
													<Input 
														type="select"
														value={selectedSeller}
														onChange={this.onChangeField}
														name="seller"
													>
														<option value="">Select Seller</option>
														{
															sellers.map(seller => {
																return (
																	<option key={seller._id} value={seller._id}>{seller.first_name} {seller.last_name}</option>
																)
															})
														}
													</Input>
												</FormGroup>
												<FormGroup>
													<div className="form-check">
														<label className="form-check-label">
																<input className="form-check-input" onChange={this.onChangeCheckbox} name="generateDownPayments" type="checkbox" checked={!!generateDownPayments} />
																Generate Down Payments for client
																<span className="form-check-sign">
																		<span className="check"></span>
																</span>
														</label>
													</div>
												</FormGroup>
												{generateDownPayments && 
													<>
														<FormGroup>
															<Label>Total Months <span className="required-start">*</span></Label>
															<Input
																type="number"
																name="totalDownPaymentMonths"
																value={totalDownPaymentMonths}
																onChange={this.onChangeField}
															/>
														</FormGroup>
														<FormGroup>
															<Label>First Payment <span className="required-start">*</span></Label>
															<Datetime
																inputProps={{
																	className: "form-control",
																	placeholder: "Date of first payment",
																	name: "firstDownPaymentDate"
																}}
																dateFormat="MM/DD/YYYY"
																timeFormat={false}
																value={typeof firstDownPaymentDate === 'string' ? moment(firstDownPaymentDate, "MM/DD/YYYY") : firstDownPaymentDate}
																onChange={(date) => this.onChangeField({target: { value: moment(date).format("MM/DD/YYYY"), name: "firstDownPaymentDate" }})}
																closeOnSelect={true}
															/>
															<span className="form-text">
																{`Subsequent payments will be generated for the amount of months provided.`}
															</span>
														</FormGroup>

														<Row>
															<Col sm={12} md={6}>
																<FormGroup>
																	<Label>Principal Amount</Label>
																	<CurrencyInput 
																		name="principalAmount"
																		price={principalAmount || 0}
																		onChange={(value) => this.onChangeField({ target: { name: "principalAmount", value } })}
																		placeholder="Total amount for principal payments"
																	/>
																</FormGroup>
															</Col>
															<Col sm={12} md={6}>
																<FormGroup>
																	<Label>First Check Number: Principal</Label>
																	<Input
																		type="number"
																		name="firstPrincipalCheckNumber"
																		value={firstPrincipalCheckNumber}
																		onChange={this.onChangeField}
																	/>
																	<span className="form-text">
																		{`This number will be used for the first payment and subsequent payments will have +1 added to the number. Leave blank if payment method is CASH`}
																	</span>
																</FormGroup>
															</Col>
															{!!firstPrincipalCheckNumber ? <Col sm={12} md={6}>
																<FormGroup>
																	<Label>Bank Name</Label>
																	<Input
																		type="text"
																		name="firstPrincipalBankName"
																		value={firstPrincipalBankName}
																		onChange={this.onChangeField}
																	/>
																</FormGroup>
															</Col> : null}
														</Row>
														<Row>
															<Col sm={12} md={6}>
																<FormGroup>
																	<Label>Misc. Amount</Label>
																	<CurrencyInput 
																		name="miscelaneousAmount"
																		price={miscelaneousAmount || 0}
																		onChange={(value) => this.onChangeField({ target: { name: "miscelaneousAmount", value } })}
																		placeholder="Total amount for miscelaneous payments"
																	/>
																</FormGroup>
															</Col>
															<Col sm={12} md={6}>
																<FormGroup>
																	<Label>First Check Number: Miscelaneous</Label>
																	<Input
																		type="number"
																		name="firstMiscCheckNumber"
																		value={firstMiscCheckNumber}
																		onChange={this.onChangeField}
																	/>
																	<span className="form-text">
																		{`This number will be used for the first payment and subsequent payments will have +1 added to the number. Leave blank if payment method is CASH`}
																	</span>
																</FormGroup>
															</Col>
															{!!firstMiscCheckNumber ? <Col sm={12} md={6}>
																<FormGroup>
																	<Label>Bank Name</Label>
																	<Input
																		type="text"
																		name="firstMiscBankName"
																		value={firstMiscBankName}
																		onChange={this.onChangeField}
																	/>
																</FormGroup>
															</Col> : null}
														</Row>
														<Row>
															<Col sm={12} md={6}>
																<FormGroup>
																	<Label>Rental Amount</Label>
																	<CurrencyInput 
																		name="rentalAmount"
																		price={rentalAmount || 0}
																		onChange={(value) => this.onChangeField({ target: { name: "rentalAmount", value } })}
																		placeholder="Total amount for rental payments"
																	/>
																</FormGroup>
															</Col>
															<Col sm={12} md={6}>
																<FormGroup>
																	<Label>First Check Number: Rental</Label>
																	<Input
																		type="number"
																		name="firstRentalCheckNumber"
																		value={firstRentalCheckNumber}
																		onChange={this.onChangeField}
																	/>
																	<span className="form-text">
																		{`This number will be used for the first payment and subsequent payments will have +1 added to the number. Leave blank if payment method is CASH`}
																	</span>
																</FormGroup>
															</Col>
															{!!firstRentalCheckNumber ? <Col sm={12} md={6}>
																<FormGroup>
																	<Label>Bank Name</Label>
																	<Input
																		type="text"
																		name="firstRentalBankName"
																		value={firstRentalBankName}
																		onChange={this.onChangeField}
																	/>
																</FormGroup>
															</Col> : null}
														</Row>
													</>
													
												}
												<FormGroup>
													<div className="form-check">
														<label className="form-check-label">
																<input className="form-check-input" onChange={this.onChangeCheckbox} name="generateBalancePayment" type="checkbox" checked={!!generateBalancePayment} />
																Generate Balance Payment for client
																<span className="form-check-sign">
																		<span className="check"></span>
																</span>
														</label>
													</div>
												</FormGroup>
												{generateBalancePayment && 
													<>
														<FormGroup>
															<Label>Subtype <span className="required-start">*</span></Label>
															<Select
																value={selectedSubtype}
																name="selectedSubtype"
																options={[
																	{
																		label: "Single",
																		value: "single"
																	},
																	{
																		label: "Multiple",
																		value: "multiple"
																	}
																]}
																className="basic-multi-select"
																classNamePrefix="select"
																onChange={this.onSelectChange('selectedSubtype')}
															/>
														</FormGroup>
														{selectedSubtype && selectedSubtype.value === "multiple" &&
															<>
																<FormGroup>
																	<Label>Total Months <span className="required-start">*</span></Label>
																	<Input
																		type="number"
																		name="totalBalancePaymentMonths"
																		value={totalBalancePaymentMonths}
																		onChange={this.onChangeField}
																		placeholder="Total of Months"
																	/>
																</FormGroup>
																<FormGroup>
																	<Label>Total Loan Interest</Label>
																	<CurrencyInput 
																		name="loan_interest"
																		price={loan_interest || 0}
																		onChange={(value) => this.onChangeField({ target: { name: "loan_interest", value } })}
																		placeholder="Total Loan Interest for all payments"
																	/>
																	<span className="form-text">
																		{`Total will be divided equally between all payments.`}
																	</span>
																</FormGroup>
															</>
														}
														<FormGroup>
															<Label>{selectedSubtype && selectedSubtype.value === "multiple" ? "Total Balance Amount" : "Total Balance Amount"} <span className="required-start">*</span></Label>
															<CurrencyInput 
																name="balanceAmount"
																price={balanceAmount || 0}
																onChange={(value) => this.onChangeField({ target: { name: "balanceAmount", value } })}
																placeholder="Total amount for balance payments"
															/>
															<span className="form-text">
																{`Total will be divided equally between all payments.`}
															</span>
														</FormGroup>
														<FormGroup>
															<Label>{selectedSubtype && selectedSubtype.value === "multiple" ? "First Balance Payment Date" : "Balance Payment Date"} <span className="required-start">*</span></Label>
															<Datetime
																inputProps={{
																	className: "form-control",
																	placeholder: "Date of balance payment",
																	name: "balancePaymentDate"
																}}
																dateFormat="MM/DD/YYYY"
																timeFormat={false}
																value={typeof balancePaymentDate === 'string' ? moment(balancePaymentDate, "MM/DD/YYYY") : balancePaymentDate}
																onChange={(date) => this.onChangeField({target: { value: moment(date).format("MM/DD/YYYY"), name: "balancePaymentDate" }})}
																closeOnSelect={true}
															/>
														</FormGroup>
														<FormGroup>
															<Label>{selectedSubtype && selectedSubtype.value === "multiple" ? "First Check Number" : "Check Number"}</Label>
															<Input
																type="number"
																name="balanceCheckNumber"
																value={balanceCheckNumber}
																onChange={this.onChangeField}
															/>
															<span className="form-text">
																{`This number will be used for the first payment and subsequent payments will have +1 added to the number. Leave blank if payment method is CASH`}
															</span>
														</FormGroup>

														{!!balanceCheckNumber 
															? <FormGroup>
																<Label>Bank Name</Label>
																<Input
																	type="text"
																	name="balanceCheckBankName"
																	value={balanceCheckBankName}
																	onChange={this.onChangeField}
																/>
																</FormGroup> 
															: null
														}

														{selectedSubtype && selectedSubtype.value === "multiple" &&
															<span className="form-text">
																{`Subsequent payments will be generated for the amount of months provided.`}
															</span>
														}
													</>
												}
												<FormGroup>
													<div className="form-check">
														<label className="form-check-label">
																<input className="form-check-input" onChange={this.onChangeCheckbox} name="generateLoanDifferencePayments" type="checkbox" checked={!!generateLoanDifferencePayments} />
																Generate Loan Difference Payments for client
																<span className="form-check-sign">
																		<span className="check"></span>
																</span>
														</label>
													</div>
												</FormGroup>
												{generateLoanDifferencePayments && 
													<>
														<FormGroup>
															<Label>Total Months <span className="required-start">*</span></Label>
															<Input
																type="number"
																name="totalLoanDifferenceMonths"
																value={totalLoanDifferenceMonths}
																onChange={this.onChangeField}
																placeholder="Total of Months"
															/>
														</FormGroup>
														<FormGroup>
															<Label>First Payment <span className="required-start">*</span></Label>
															<Datetime
																inputProps={{
																	className: "form-control",
																	placeholder: "Date of first payment",
																	name: "firstLoanDifferencePaymentDate"
																}}
																dateFormat="MM/DD/YYYY"
																timeFormat={false}
																value={typeof firstLoanDifferencePaymentDate === 'string' ? moment(firstLoanDifferencePaymentDate, "MM/DD/YYYY") : firstLoanDifferencePaymentDate}
																onChange={(date) => this.onChangeField({target: { value: moment(date).format("MM/DD/YYYY"), name: "firstLoanDifferencePaymentDate" }})}
																closeOnSelect={true}
															/>
															<span className="form-text">
																{`Subsequent payments will be generated for the amount of months provided.`}
															</span>
														</FormGroup>
														<FormGroup>
															<Label>Total Amount <span className="required-start">*</span></Label>
															<CurrencyInput 
																name="loanDifferenceAmount"
																price={loanDifferenceAmount || 0}
																onChange={(value) => this.onChangeField({ target: { name: "loanDifferenceAmount", value } })}
																placeholder="Total amount (will be divided equally between all payments)"
															/>
															<span className="form-text">
																{`Total will be divided equally between all payments.`}
															</span>
														</FormGroup>
													</>
												}
												<FormGroup>
													<div className="form-check">
														<label className="form-check-label">
																<input className="form-check-input" onChange={this.onChangeCheckbox} name="generatePenaltyPayments" type="checkbox" checked={!!generatePenaltyPayments} />
																Generate Penalty Payments for client
																<span className="form-check-sign">
																		<span className="check"></span>
																</span>
														</label>
													</div>
												</FormGroup>
												{generatePenaltyPayments && 
													<>
														<FormGroup>
															<Label>Total Months <span className="required-start">*</span></Label>
															<Input
																type="number"
																name="totalPenaltyMonths"
																value={totalPenaltyMonths}
																onChange={this.onChangeField}
																placeholder="Total of Months"
															/>
														</FormGroup>
														<FormGroup>
															<Label>First Payment <span className="required-start">*</span></Label>
															<Datetime
																inputProps={{
																	className: "form-control",
																	placeholder: "Date of first payment",
																	name: "firstPenaltyPaymentDate"
																}}
																dateFormat="MM/DD/YYYY"
																timeFormat={false}
																value={typeof firstPenaltyPaymentDate === 'string' ? moment(firstPenaltyPaymentDate, "MM/DD/YYYY") : firstPenaltyPaymentDate}
																onChange={(date) => this.onChangeField({target: { value: moment(date).format("MM/DD/YYYY"), name: "firstPenaltyPaymentDate" }})}
																closeOnSelect={true}
															/>
															<span className="form-text">
																{`Subsequent payments will be generated for the amount of months provided.`}
															</span>
														</FormGroup>
														<FormGroup>
															<Label>Total Amount <span className="required-start">*</span></Label>
															<CurrencyInput 
																name="penaltyAmount"
																price={penaltyAmount || 0}
																onChange={(value) => this.onChangeField({ target: { name: "penaltyAmount", value } })}
																placeholder="Total amount (will be divided equally between all payments)"
															/>
															<span className="form-text">
																{`Total will be divided equally between all payments.`}
															</span>
														</FormGroup>
													</>
												}
												<FormGroup>
													<div className="form-check">
														<label className="form-check-label">
																<input className="form-check-input" onChange={this.onChangeCheckbox} name="generateCounterOfferPayments" type="checkbox" checked={!!generateCounterOfferPayments} />
																Generate Counter Offer Payments for client
																<span className="form-check-sign">
																		<span className="check"></span>
																</span>
														</label>
													</div>
												</FormGroup>
												{generateCounterOfferPayments && 
													<>
														<FormGroup>
															<Label>Total Months <span className="required-start">*</span></Label>
															<Input
																type="number"
																name="totalCounterOfferMonths"
																value={totalCounterOfferMonths}
																onChange={this.onChangeField}
																placeholder="Total of Months"
															/>
														</FormGroup>
														<FormGroup>
															<Label>First Payment <span className="required-start">*</span></Label>
															<Datetime
																inputProps={{
																	className: "form-control",
																	placeholder: "Date of first payment",
																	name: "firstCounterOfferPaymentDate"
																}}
																dateFormat="MM/DD/YYYY"
																timeFormat={false}
																value={typeof firstCounterOfferPaymentDate === 'string' ? moment(firstCounterOfferPaymentDate, "MM/DD/YYYY") : firstCounterOfferPaymentDate}
																onChange={(date) => this.onChangeField({target: { value: moment(date).format("MM/DD/YYYY"), name: "firstCounterOfferPaymentDate" }})}
																closeOnSelect={true}
															/>
															<span className="form-text">
																{`Subsequent payments will be generated for the amount of months provided.`}
															</span>
														</FormGroup>
														<FormGroup>
															<Label>Total Amount <span className="required-start">*</span></Label>
															<CurrencyInput 
																name="counterOfferAmount"
																price={counterOfferAmount || 0}
																onChange={(value) => this.onChangeField({ target: { name: "counterOfferAmount", value } })}
																placeholder="Total amount (will be divided equally between all payments)"
															/>
															<span className="form-text">
																{`Total will be divided equally between all payments.`}
															</span>
														</FormGroup>
													</>
												}
												
										</>
									}
									{_id 
									? 	<>
											<FormGroup>
												<Label>Project</Label>
												<Async
													value={selectedProject}
													name="selectedProject"
													options={[]}
													loadOptions={this.loadProjects}
													className="basic-multi-select"
													classNamePrefix="select"
													onChange={this.onSelectChange('selectedProject')}
													placeholder="Search project by name"
													isDisabled
												/>
											</FormGroup>
											<FormGroup>
												<Label>Edit note <span className="required-start">*</span></Label>
												<Input
													type="text"
													name="editNote"
													value={editNote || ""}
													onChange={this.onChangeField}
												/>
											</FormGroup>
											<FormGroup>
												<Label>Created At</Label>
												<Input
													type="text"
													name="created_at"
													value={getFormattedDate(created_at || '')}
													readOnly
												/>
											</FormGroup>
											<FormGroup>
												<Label>Updated At</Label>
												<Input
													type="text"
													name="updated_at"
													value={getFormattedDate(updated_at || '')}
													readOnly
												/>
											</FormGroup>
										</> 
									: null}
									{error ? <div className="alert alert-danger mt-3">
											<span>{error}</span>
										</div> : null
									}
								</CardBody>
								{payment ? <Col>
									<Button color="primary" block={false} className="mb-4" onClick={this.save} disabled={saving}>
										{saving
											? "Saving..."
											: _id
												? "Save Changes"
												: "Save Payment"
										}
									</Button>
								</Col> : null}
							</Card>
						</Col>
					</Row>
				</div>
			</>
		);
	}
}

export default PaymentForm;
